import {useCallback, useEffect, useMemo, useState} from "react"
import axios from "axios"
import moment from "moment"
import {Alert, Badge, Card, Form, Spinner, Table} from "react-bootstrap"
import {useTranslation} from "react-i18next"

import {useAuth} from "../../context/AuthProvider"

type CachedExam = {
    start: string
    end?: string
    titles: string[]
    roomIdentifiers: string[]
    lastFetchedAt: string
}

export default function TucanCachedExamsComponent() {
    const {t, i18n} = useTranslation("admin")
    const auth = useAuth()
    const isAdmin = auth.hasGate("admin")
    const [exams, setExams] = useState<CachedExam[]>([])
    const [query, setQuery] = useState("")
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const load = useCallback(async () => {
        if (!isAdmin) {
            setLoading(false)
            return
        }
        try {
            const response = await axios.get<CachedExam[]>(
                "/api/schedule/tucan-bookings",
                auth.axiosAuthHeader()
            )
            setExams(response.data)
            setError(null)
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.msg ?? t("tucanCachedExams.messages.loadError")
                : t("tucanCachedExams.messages.loadError"))
        } finally {
            setLoading(false)
        }
    }, [auth, isAdmin, t])

    useEffect(() => {
        void load()
    }, [load])

    const filtered = useMemo(() => {
        const term = query.trim().toLocaleLowerCase(i18n.language)
        if (!term) return exams
        return exams.filter(exam => [
            ...exam.titles,
            ...exam.roomIdentifiers,
            moment(exam.start).format("L"),
        ].some(value => value.toLocaleLowerCase(i18n.language).includes(term)))
    }, [exams, i18n.language, query])

    if (!isAdmin) return <div className="component">
        <Alert variant="danger">{t("adminOnly")}</Alert>
    </div>

    return <div className="component">
        <Card>
            <Card.Header>
                <h3 className="mb-1"><i className="bi bi-database me-2"/>
                    {t("tucanCachedExams.title")}
                </h3>
                <div className="text-muted">
                    {t("tucanCachedExams.description")}
                </div>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form.Control type="search" className="mb-3"
                    placeholder={t("tucanCachedExams.search")}
                    value={query} onChange={event => setQuery(event.target.value)}/>
                {loading ? <div><Spinner size="sm" className="me-2"/>
                    {t("tucanCachedExams.loading")}</div> :
                    <Table responsive hover>
                        <thead><tr><th>{t("tucanCachedExams.columns.exam")}</th>
                            <th>{t("tucanCachedExams.columns.date")}</th>
                            <th>{t("tucanCachedExams.columns.time")}</th>
                            <th>{t("tucanCachedExams.columns.rooms")}</th>
                            <th>{t("tucanCachedExams.columns.cachedAt")}</th></tr></thead>
                        <tbody>
                        {filtered.map((exam, index) => <tr
                            key={`${exam.start}-${exam.end}-${exam.titles.join("|")}-${index}`}>
                            <td>{exam.titles.join(" · ")}</td>
                            <td>{moment(exam.start).format("L")}</td>
                            <td>{moment(exam.start).format("LT")}
                                {exam.end ? `–${moment(exam.end).format("LT")}` : ""}</td>
                            <td><div className="d-flex flex-wrap gap-1">
                                {exam.roomIdentifiers.map(identifier =>
                                    <Badge bg="secondary" key={identifier}>{identifier}</Badge>)}
                            </div></td>
                            <td>{moment(exam.lastFetchedAt).format("L LTS")}</td>
                        </tr>)}
                        {filtered.length === 0 && <tr><td colSpan={5} className="text-muted">
                            {t("tucanCachedExams.empty")}
                        </td></tr>}
                        </tbody>
                    </Table>}
            </Card.Body>
        </Card>
    </div>
}
